<script setup>
import { computed, onMounted, ref } from 'vue'
import MapStage from './components/MapStage.vue'
import eventsSource from '../../long_march_events.v1.json'
import locationsSource from '../../long_march_event_locations.v1.json'
import routeRaw from '../../central_red_army_route.v1.geojson?raw'

const routeData = JSON.parse(routeRaw)
const locationById = new Map([
  ...locationsSource.locations,
  ...locationsSource.epilogueLocations
].map((item) => [item.id, item]))

const categoryLabels = {
  departure: '出发',
  battle: '战役战斗',
  meeting: '会议决策',
  march: '行军',
  arrival: '抵达'
}

const events = eventsSource.events.map((event) => ({
  ...event,
  location: locationById.get(event.id),
  categoryLabel: categoryLabels[event.category] ?? event.category
}))

const activeId = ref(events[0].id)
const activeCategory = ref('all')
const introVisible = ref(new URLSearchParams(window.location.search).get('intro') !== '0')
const auditNumber = import.meta.env.VITE_TDT_AUDIT_NUMBER || '发布前核验审图号'

const categories = computed(() => [
  { id: 'all', label: '全部' },
  ...Array.from(new Set(events.map((item) => item.category))).map((id) => ({
    id,
    label: categoryLabels[id] ?? id
  }))
])

const visibleEvents = computed(() => activeCategory.value === 'all'
  ? events
  : events.filter((event) => event.category === activeCategory.value))

const activeEvent = computed(() => events.find((event) => event.id === activeId.value) ?? events[0])

const formattedDate = computed(() => {
  const event = activeEvent.value
  if (!event.endDate || event.startDate === event.endDate) return event.startDate
  return `${event.startDate} — ${event.endDate}`
})

function selectEvent(id) {
  activeId.value = id
  introVisible.value = false
}

function selectCategory(id) {
  activeCategory.value = id
  const first = id === 'all' ? events[0] : events.find((event) => event.category === id)
  if (first) activeId.value = first.id
}

onMounted(() => {
  window.setTimeout(() => { introVisible.value = false }, 3200)
})
</script>

<template>
  <main class="app-shell">
    <div class="ambient ambient-one"></div>
    <div class="ambient ambient-two"></div>

    <header class="topbar">
      <div class="brand-lockup">
        <span class="brand-mark" aria-hidden="true">征</span>
        <div>
          <h1>数智长征 <span>· 青春接力</span></h1>
        </div>
      </div>
      <div class="top-stats" aria-label="数据概览">
        <div><strong>1934—1935</strong><span>中央红军主线</span></div>
      </div>
      <button class="about-button" type="button" @click="introVisible = !introVisible">
        {{ introVisible ? '进入地图' : '开始' }}
      </button>
    </header>

    <section class="workspace">
      <aside class="side-panel event-index">
        <div class="panel-heading">
          <div>
            <h2>征程节点</h2>
          </div>
          <span class="count-badge">{{ visibleEvents.length }}</span>
        </div>

        <div class="category-filter" aria-label="事件分类筛选">
          <button
            v-for="category in categories"
            :key="category.id"
            type="button"
            :class="{ active: activeCategory === category.id }"
            @click="selectCategory(category.id)"
          >
            {{ category.label }}
          </button>
        </div>

        <div class="event-list">
          <button
            v-for="event in visibleEvents"
            :key="event.id"
            type="button"
            class="event-row"
            :class="{ active: activeId === event.id }"
            @click="selectEvent(event.id)"
          >
            <span class="event-order">{{ String(event.order).padStart(2, '0') }}</span>
            <span class="event-row-copy">
              <strong>{{ event.title }}</strong>
              <small>{{ event.startDate }} · {{ event.currentPlace.split('；')[0] }}</small>
            </span>
            <span class="event-dot"></span>
          </button>
        </div>
      </aside>

      <section class="map-column">
        <MapStage
          :events="events"
          :route-data="routeData"
          :active-id="activeId"
          @select="selectEvent"
        />
        <div class="map-legend" aria-label="地图图例">
          <span><i class="legend-route"></i>中央红军主线示意</span>
          <span><i class="legend-site"></i>重要节点</span>
        </div>
      </section>

      <aside class="side-panel detail-panel">
        <div class="detail-topline">
          <span class="event-tag">{{ activeEvent.categoryLabel }}</span>
          <span class="event-sequence">NO. {{ String(activeEvent.order).padStart(2, '0') }}</span>
        </div>
        <p class="detail-date">{{ formattedDate }}</p>
        <h2>{{ activeEvent.title }}</h2>
        <p class="detail-summary">{{ activeEvent.summary }}</p>

        <dl class="detail-facts">
          <div>
            <dt>历史地点</dt>
            <dd>{{ activeEvent.historicalPlace }}</dd>
          </div>
          <div>
            <dt>现今位置</dt>
            <dd>{{ activeEvent.currentPlace }}</dd>
          </div>
        </dl>

      </aside>
    </section>

    <footer class="footerbar">
      <div class="footer-note">
        <span>数据说明</span>
        仅展示中央红军1934—1935主线。部分信息存在争议，均采用主流说法。地图为示意展示，非测绘成果。
      </div>
      <div class="audit-mark">
        © 天地图 · {{ auditNumber }}
      </div>
    </footer>

    <Transition name="intro">
      <section v-if="introVisible" class="intro-overlay" @click.self="introVisible = false">
        <div class="intro-card">
          <span class="intro-number">25,000</span>
          <span class="intro-unit">里 · 不是一个抽象数字</span>
          <h2>让历史在地图上重新被看见</h2>
          <p>以权威史料为经纬，以代码为新的叙事方式。拖动地图、点击节点，沿中央红军长征主线回望关键抉择与艰难跋涉。</p>
          <button type="button" @click="introVisible = false">开始浏览</button>
        </div>
      </section>
    </Transition>
  </main>
</template>
